import {
  OFFER_BUSINESS_CONTEXT_LABELS,
  OFFER_KIND_LABELS,
  OFFER_MACRO_STATUS,
  OFFER_PAYMENT_KIND_LABELS,
  OFFER_STEPS_BY_STATUS,
  OFFER_STATUS_META,
  PROPERTY_OFFER_BUSINESS_CONTEXT,
  PROPERTY_OFFER_PAYMENT_KIND,
  PROPERTY_OFFER_STATUS,
} from 'dashboard/constants/lais/offer';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { referenceOfferStatusByStep } from 'dashboard/routes/dashboard/laisTenantsRentFlows/utils/referenceOfferLogic';
import { fallbackLabel } from './primitiveHelpers';

const present = value => value !== undefined && value !== null && value !== '';
const firstPresent = (...values) => values.find(present);

const translatedLabel = (meta, t) => {
  if (!meta) return null;
  if (typeof t === 'function' && meta.labelKey) {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return t(meta.labelKey);
  }
  return meta.label;
};

export const getOfferStatusDescriptor = (status, { t } = {}) => {
  const meta = OFFER_STATUS_META[status] || OFFER_STATUS_META.submitted;

  return {
    status: status || 'submitted',
    label: translatedLabel(meta, t),
    labelKey: meta.labelKey,
    variant: meta.variant,
    className: meta.className,
  };
};

export const getOfferStatusLabel = (status, options = {}) =>
  getOfferStatusDescriptor(status, options).label;

export const getOfferStatusClass = status =>
  getOfferStatusDescriptor(status).className;

export const getOfferKindLabel = kind =>
  OFFER_KIND_LABELS[kind] || fallbackLabel(kind, 'Proposta');

export const getOfferBusinessContextLabel = context =>
  OFFER_BUSINESS_CONTEXT_LABELS[context] || fallbackLabel(context, 'Geral');

export const getOfferPaymentKindLabel = paymentKind =>
  OFFER_PAYMENT_KIND_LABELS[paymentKind] ||
  fallbackLabel(paymentKind, 'Não informado');

export const resolveBuyerId = (offer = {}) =>
  firstPresent(
    offer.buyer_id,
    offer.buyerId,
    offer.buyer?.external_id,
    offer.buyer?.externalId,
    offer.buyer?.id,
    offer.contact_id,
    offer.contactId,
    offer.contact?.id
  );

export const resolveOfferPrice = (offer = {}) =>
  firstPresent(
    offer.offer_amount,
    offer.offerAmount,
    offer.final_price,
    offer.finalPrice,
    offer.offer_price,
    offer.offerPrice,
    offer.offer?.final_price,
    offer.offer?.finalPrice,
    offer.offer?.offer_price,
    offer.offer?.offerPrice
  );

export const resolveOfferPaymentKind = (offer = {}) =>
  firstPresent(
    offer.payment_kind,
    offer.paymentKind,
    offer.payment?.payment_kind,
    offer.payment?.paymentKind,
    offer.payment?.payment_method,
    offer.payment?.paymentMethod
  );

export const resolveOfferPaymentLabel = offer =>
  getOfferPaymentKindLabel(resolveOfferPaymentKind(offer));

export const resolveOfferStepKey = (offer = {}) =>
  firstPresent(
    offer.sales_flow_step,
    offer.salesFlowStep,
    offer.current_step?.key,
    offer.currentStep?.key,
    offer.step?.key,
    offer.status
  );

const stepIn = (step, status) =>
  (OFFER_STEPS_BY_STATUS[status] || []).includes(String(step || ''));

export const getStatusByStep = (step, ccv = null) => {
  const referenceStatus = referenceOfferStatusByStep(step, ccv);
  if (referenceStatus) return referenceStatus;

  if (stepIn(step, OFFER_MACRO_STATUS.CANCELED)) {
    return OFFER_MACRO_STATUS.CANCELED;
  }
  if (stepIn(step, OFFER_MACRO_STATUS.FINISHED)) {
    return OFFER_MACRO_STATUS.FINISHED;
  }
  if (stepIn(step, OFFER_MACRO_STATUS.CCV)) {
    return OFFER_MACRO_STATUS.CCV;
  }
  if (stepIn(step, OFFER_MACRO_STATUS.OFFER_ACCEPTED)) {
    return ccv ? OFFER_MACRO_STATUS.CCV : OFFER_MACRO_STATUS.OFFER_ACCEPTED;
  }
  if (stepIn(step, OFFER_MACRO_STATUS.NEGOTIATION)) {
    return OFFER_MACRO_STATUS.NEGOTIATION;
  }
  return OFFER_MACRO_STATUS.NEW;
};

export const offerAlertDescriptor = (offer = {}) => {
  const step = resolveOfferStepKey(offer);
  const status = offer.status;
  if (
    stepIn(step, OFFER_MACRO_STATUS.CANCELED) ||
    [PROPERTY_OFFER_STATUS.DECLINED, PROPERTY_OFFER_STATUS.WITHDRAWN].includes(
      status
    )
  ) {
    return {
      kind: 'critical',
      titleKey: 'PROPERTIES.OFFERS.ALERT.CANCELED_TITLE',
      messageKey: 'PROPERTIES.OFFERS.ALERT.CANCELED_MESSAGE',
    };
  }
  if (
    stepIn(step, OFFER_MACRO_STATUS.FINISHED) ||
    [PROPERTY_OFFER_STATUS.SIGNED].includes(status)
  ) {
    return {
      kind: 'positive',
      titleKey: 'PROPERTIES.OFFERS.ALERT.FINISHED_TITLE',
      messageKey: 'PROPERTIES.OFFERS.ALERT.FINISHED_MESSAGE',
    };
  }

  return null;
};

export const resolveListingAddress = (offer = {}) => {
  const house = offer.imovel || offer.house || {};
  const address = firstPresent(
    house.endereco,
    house.street,
    house.address,
    offer.imovel_endereco
  );
  const number = firstPresent(house.numero, house.number);
  const neighbourhood = firstPresent(
    house.bairro,
    house.neighborhood,
    house.neighbourhood
  );
  const city = firstPresent(house.cidade, house.city);
  const parts = [
    [address, number].filter(Boolean).join(', '),
    [neighbourhood, city].filter(Boolean).join(', '),
  ].filter(Boolean);

  return parts.join(' - ');
};

export const resolveListingId = (offer = {}) =>
  firstPresent(
    offer.imovel?.external_id,
    offer.imovel?.externalId,
    offer.house?.external_id,
    offer.house?.externalId,
    offer.imovel_id,
    offer.houseId
  );

export const getListingLink = (offer, accountId) => {
  const normalizedOffer = offer || {};
  const listingId = firstPresent(
    normalizedOffer.imovel?.id,
    normalizedOffer.imovel_id
  );
  if (!listingId || !accountId) return '';
  return frontendURL(`accounts/${accountId}/imoveis/${listingId}`);
};

const dateForMacroStatus = (offer, macroStatus) => {
  const nestedOffer = offer.offer || {};
  if (
    [OFFER_MACRO_STATUS.NEW, OFFER_MACRO_STATUS.NEGOTIATION].includes(
      macroStatus
    )
  ) {
    return firstPresent(
      offer.proposal_date,
      offer.proposalDate,
      nestedOffer.proposal_date,
      nestedOffer.proposalDate
    );
  }
  if (macroStatus === OFFER_MACRO_STATUS.OFFER_ACCEPTED) {
    return firstPresent(
      offer.accepted_at,
      offer.acceptedAt,
      nestedOffer.accepted_at,
      nestedOffer.acceptedAt
    );
  }
  if (macroStatus === OFFER_MACRO_STATUS.CCV) {
    return firstPresent(offer.ccv?.signed_at, offer.ccv?.signedAt);
  }
  if (macroStatus === OFFER_MACRO_STATUS.CANCELED) {
    return firstPresent(
      offer.discarded_at,
      offer.discardedAt,
      nestedOffer.discarded_at,
      nestedOffer.discardedAt
    );
  }
  if (macroStatus === OFFER_MACRO_STATUS.FINISHED) {
    return firstPresent(offer.updated_at, offer.updatedAt);
  }
  return '';
};

const labelForMacroStatus = macroStatus =>
  ({
    [OFFER_MACRO_STATUS.NEW]: 'Nova proposta',
    [OFFER_MACRO_STATUS.NEGOTIATION]: 'Em negociação',
    [OFFER_MACRO_STATUS.OFFER_ACCEPTED]: 'Proposta aceita',
    [OFFER_MACRO_STATUS.CCV]: 'Simulação e análise de crédito',
    [OFFER_MACRO_STATUS.CANCELED]: 'Proposta cancelada',
    [OFFER_MACRO_STATUS.FINISHED]: 'Proposta finalizada',
  })[macroStatus] || 'Status da proposta';

const STEP_STATUS_COPY = Object.freeze({
  NEGOTIATION_NEW: Object.freeze({
    label: 'Nova proposta',
    startedLabel: 'Nova proposta',
    description: 'A proposta foi enviada pelo comprador.',
  }),
  PROPOSAL_ON_VALIDATION: Object.freeze({
    label: 'Nova proposta',
    startedLabel: 'Nova proposta',
    description: 'A proposta foi enviada pelo comprador.',
  }),
  submitted: Object.freeze({
    label: 'Nova proposta',
    startedLabel: 'Nova proposta',
    description: 'A proposta foi enviada pelo comprador.',
  }),
  nova: Object.freeze({
    label: 'Nova proposta',
    startedLabel: 'Nova proposta',
    description: 'A proposta foi enviada pelo comprador.',
  }),
  NEGOTIATION_ONGOING: Object.freeze({
    label: 'Negociação',
    startedLabel: 'Em negociação',
    description:
      'Quando o executivo de negociação entra em contato com o comprador e proprietário.',
  }),
  PROPOSAL_ONGOING: Object.freeze({
    label: 'Negociação',
    startedLabel: 'Em negociação',
    description:
      'Quando o executivo de negociação entra em contato com o comprador e proprietário.',
  }),
  negociacao: Object.freeze({
    label: 'Negociação',
    startedLabel: 'Em negociação',
    description:
      'Quando o executivo de negociação entra em contato com o comprador e proprietário.',
  }),
  NEGOTIATION_OFFER_ACCEPTED: Object.freeze({
    label: 'Aceite da proposta',
    startedLabel: 'Proposta aceita',
    description: 'A proposta é analisada pelo o comprador e vendedor.',
  }),
  PROPOSAL_ACCEPTED: Object.freeze({
    label: 'Aceite da proposta',
    startedLabel: 'Proposta aceita',
    description: 'A proposta é analisada pelo o comprador e vendedor.',
  }),
  accepted: Object.freeze({
    label: 'Aceite da proposta',
    startedLabel: 'Proposta aceita',
    description: 'A proposta é analisada pelo o comprador e vendedor.',
  }),
  aceita: Object.freeze({
    label: 'Aceite da proposta',
    startedLabel: 'Proposta aceita',
    description: 'A proposta é analisada pelo o comprador e vendedor.',
  }),
  CREDIT_ANALYSIS: Object.freeze({
    label: 'Simulação e análise de crédito',
    startedLabel: 'Simulação e análise de crédito',
    description:
      'Envio de simulação e análise do banco escolhido para obtenção do crédito para o comprador. Prazo estimado: 2 - 15 dias úteis',
  }),
  analise_credito: Object.freeze({
    label: 'Simulação e análise de crédito',
    startedLabel: 'Simulação e análise de crédito',
    description:
      'Envio de simulação e análise do banco escolhido para obtenção do crédito para o comprador. Prazo estimado: 2 - 15 dias úteis',
  }),
  CCV_SENT_TO_BE_SIGNED: Object.freeze({
    label:
      'Envio de documentos e assinatura do Compromisso de Compra e Venda (CCV)',
    startedLabel:
      'Envio de documentos e assinatura do Compromisso de Compra e Venda (CCV)',
  }),
  ccv_enviado: Object.freeze({
    label:
      'Envio de documentos e assinatura do Compromisso de Compra e Venda (CCV)',
    startedLabel:
      'Envio de documentos e assinatura do Compromisso de Compra e Venda (CCV)',
  }),
  CCV_SIGNED: Object.freeze({
    label: 'Assinatura do compromisso de Compra e Venda (CCV)',
    startedLabel: 'Compromisso de Compra e Venda (CCV) assinado',
  }),
  ccv_assinado: Object.freeze({
    label: 'Assinatura do compromisso de Compra e Venda (CCV)',
    startedLabel: 'Compromisso de Compra e Venda (CCV) assinado',
  }),
  OFFER_FINISHED: Object.freeze({
    label: 'Proposta finalizada',
    startedLabel: 'Proposta finalizada',
    description:
      'Parabéns! O Compromisso de Compra e Venda (CCV) foi assinado. Agora, o processo seguirá para as últimas etapas visando a conclusão da venda.',
  }),
  PURCHASE_SALE_COMPLETED: Object.freeze({
    label: 'Proposta finalizada',
    startedLabel: 'Proposta finalizada',
    description:
      'Parabéns! O Compromisso de Compra e Venda (CCV) foi assinado. Agora, o processo seguirá para as últimas etapas visando a conclusão da venda.',
  }),
  POST_CCV_HUB_CT: Object.freeze({
    label: 'Proposta finalizada',
    startedLabel: 'Proposta finalizada',
    description:
      'Parabéns! O Compromisso de Compra e Venda (CCV) foi assinado. Agora, o processo seguirá para as últimas etapas visando a conclusão da venda.',
  }),
  POST_CCV: Object.freeze({
    label: 'Proposta finalizada',
    startedLabel: 'Proposta finalizada',
    description:
      'Parabéns! O Compromisso de Compra e Venda (CCV) foi assinado. Agora, o processo seguirá para as últimas etapas visando a conclusão da venda.',
  }),
  finalizada: Object.freeze({
    label: 'Proposta finalizada',
    startedLabel: 'Proposta finalizada',
    description:
      'Parabéns! O Compromisso de Compra e Venda (CCV) foi assinado. Agora, o processo seguirá para as últimas etapas visando a conclusão da venda.',
  }),
  tenantDidntSentOffer: Object.freeze({
    label: 'Interessado não finalizou envio da proposta',
    description:
      'Interessado deu início a proposta mas falta finalizar o envio',
  }),
  waitingOwnerResponse: Object.freeze({
    label: 'Aguardando resposta do proprietário',
    description: 'Proprietário precisa responder a proposta',
  }),
  waitingTenantRespondCounterOffer: Object.freeze({
    label: 'Contra-proposta feita pelo proprietário',
    description: 'Interessado precisa responder a contra-proposta',
  }),
  waitingOwnerRespondCounterOffer: Object.freeze({
    label: 'Contra-proposta feita pelo interessado',
    description: 'Proprietário precisa responder a contra-proposta',
  }),
  ownerRejectedOffer: Object.freeze({
    label: 'Proposta rejeitada',
  }),
  tenantRejectedOffer: Object.freeze({
    label: 'Proposta rejeitada',
  }),
  waitingForTenantToDecide: Object.freeze({
    label: 'Aguardando interessado decidir sobre imóvel',
  }),
  offerDemandPending: Object.freeze({
    label: 'Aguardando resposta do cliente',
  }),
  offerAccepted: Object.freeze({
    label: 'Proposta aceita',
  }),
  proposalCancelled: Object.freeze({
    label: 'Proposta cancelada',
  }),
  stalledRejection: Object.freeze({
    label: 'Proposta cancelada',
  }),
  houseInactive: Object.freeze({
    label: 'Proposta cancelada',
    description: 'Proposta cancelada por suspensão do imóvel',
  }),
  houseUnpublished: Object.freeze({
    label: 'Proposta cancelada',
    description: 'Proposta cancelada por despublicação do imóvel',
  }),
  docsDemandPending: Object.freeze({
    label: 'Documentos pendentes do cliente',
    description: 'Interessado precisa enviar a documentação',
  }),
  waitingDocumentations: Object.freeze({
    label: 'Aguardando envio de documentos',
    description: 'Aguardando envio de documentos',
  }),
  waitingTenantDocumentation: Object.freeze({
    label: 'Aguardando documentos do interessado',
    description: 'Interessado precisa enviar a documentação',
  }),
  waitingOwnerDocumentation: Object.freeze({
    label: 'Aguardando documentos do proprietário',
    description: 'Proprietário precisa enviar a documentação',
  }),
  waitingResendDocumentations: Object.freeze({
    label: 'Aguardando reenvio de documentos',
    description: 'Aguardando reenvio de documentos',
  }),
  waitingTenantResendDocumentation: Object.freeze({
    label: 'Aguardando interessado reenviar documentação',
    description: 'Interessado precisa reenviar a documentação',
  }),
  waitingOwnerResendDocumentation: Object.freeze({
    label: 'Aguardando proprietário reenviar documentação',
    description: 'Proprietário precisa reenviar a documentação',
  }),
  docsDemandResend: Object.freeze({
    label: 'Documentos com pendência',
    description: 'Interessado precisa reenviar a documentação',
  }),
  docsSupplyPending: Object.freeze({
    label: 'Documentos pendentes do proprietário',
    description: 'Proprietário precisa enviar a documentação',
  }),
  docsQuintoandarAnalysis: Object.freeze({
    label: 'Documentação em análise',
    description: 'Interessado com documentação em análise (3 dias úteis)',
  }),
  documentationsInAnalysis: Object.freeze({
    label: 'Documentações em análise',
    description: 'Documentações em análise',
  }),
  tenantDocumentationInAnalysisChip: Object.freeze({
    label: 'Documentos do interessado em análise',
    description: 'Interessado com documentação em análise (3 dias úteis)',
  }),
  ownerDocumentationInAnalysisChip: Object.freeze({
    label: 'Documentos do proprietário em análise',
    description: 'Proprietário com documentação em análise (3 dias úteis)',
  }),
  docsApproved: Object.freeze({
    label: 'Documentação aprovada',
    description: 'Documentação pronta para o contrato',
  }),
  documentationsRefused: Object.freeze({
    label: 'Documentações recusadas',
    description: 'Documentações recusadas',
  }),
  tenantRejected: Object.freeze({
    label: 'Recusado na análise de crédito',
    description: 'Recusado na análise de crédito',
  }),
  tenantWithMultipleDocs: Object.freeze({
    label: 'Interessado com documentação em mais de um imóvel',
    description: 'Interessado com documentação em mais de um imóvel',
  }),
  guaranteeCreated: Object.freeze({
    label: 'Garantia emitida',
  }),
  guaranteeDemandPaymentPending: Object.freeze({
    label: 'Aguardando pagamento da garantia',
  }),
  guaranteePaid: Object.freeze({
    label: 'Garantia paga',
  }),
  waitingContract: Object.freeze({
    label: 'Aguardando contrato',
  }),
  contractQuintoandarInCreation: Object.freeze({
    label: 'Contrato em elaboração',
  }),
  contractAllSignaturesPending: Object.freeze({
    label: 'Contrato aguardando assinaturas',
    description: 'Aguardando ambas assinaturas',
  }),
  contractSupplySignaturePending: Object.freeze({
    label: 'Aguardando assinatura do proprietário',
    description: 'Proprietário precisa assinar o contrato',
  }),
  contractDemandSignaturePending: Object.freeze({
    label: 'Aguardando assinatura do cliente',
    description: 'Interessado precisa assinar o contrato',
  }),
  waitingSignaturesValidation: Object.freeze({
    label: 'Aguardando validação das assinaturas',
    description: 'Aguardando validação das assinaturas',
  }),
  ownerSignedContract: Object.freeze({
    label: 'Proprietário finalizou a assinatura do contrato',
  }),
  tenantSignedContract: Object.freeze({
    label: 'Interessado finalizou a assinatura do contrato',
  }),
  contractStartDateReached: Object.freeze({
    label: 'Contrato ativo',
  }),
  contractSigned: Object.freeze({
    label: 'Contrato assinado',
  }),
  contractTerminated: Object.freeze({
    label: 'Contrato finalizado',
  }),
  contractCancelled: Object.freeze({
    label: 'Contrato cancelado',
  }),
});

const CANCELED_STEP_DESCRIPTIONS = Object.freeze({
  CREDIT_NOT_APPROVED: 'Crédito não aprovado',
  BUYER_GAVE_UP_TO_BUY_THIS_PROPERTY:
    'O comprador desistiu de comprar este imóvel',
  SELLER_ONLY_ACCEPTS_LISTING_PRICE:
    'O proprietário só aceita o preço do anúncio',
  COUNTER_OFFER_REJECTED: 'Contraproposta recusada',
  NO_RESPONSE: 'Sem resposta',
  GAVE_UP_OFFER: 'Desistiu da oferta',
  DEAL_WITH_ANOTHER_REAL_STATE: 'Em negociação com outra imobiliária',
  ALREADY_DEALT_WITH_ANOTHER_PERSON: 'Já negociou com outra pessoa',
  PROBLEMATIC_HOUSE_DOCUMENTATION: 'Problemas com documentação do imóvel',
  SUSPENDED_SELL: 'Venda suspensa',
  DEFAULT_CANCELED_REASON:
    'Consulte seu executivo de negociação para mais informações',
});

const stepStatusCopy = (step, macroStatus, hasDate) => {
  if (macroStatus === OFFER_MACRO_STATUS.CANCELED) {
    return {
      label: 'Proposta cancelada',
      description:
        CANCELED_STEP_DESCRIPTIONS[step] ||
        CANCELED_STEP_DESCRIPTIONS.DEFAULT_CANCELED_REASON,
    };
  }

  const copy = STEP_STATUS_COPY[step];
  if (!copy) return { label: labelForMacroStatus(macroStatus) };

  return {
    label: hasDate && copy.startedLabel ? copy.startedLabel : copy.label,
    description: copy.description,
  };
};

export const buildFallbackOfferStatusStep = (offer = {}) => {
  const step = resolveOfferStepKey(offer);
  const macroStatus = getStatusByStep(step, offer.ccv || offer.ccv_contract);
  const date = dateForMacroStatus(offer, macroStatus) || null;
  let state = 'pendente';
  if (macroStatus === OFFER_MACRO_STATUS.CANCELED) {
    state = 'cancelado';
  } else if (
    [
      OFFER_MACRO_STATUS.OFFER_ACCEPTED,
      OFFER_MACRO_STATUS.CCV,
      OFFER_MACRO_STATUS.FINISHED,
    ].includes(macroStatus)
  ) {
    state = 'concluido';
  }
  const copy = stepStatusCopy(step, macroStatus, Boolean(date));

  return {
    key: step || macroStatus,
    label: copy.label,
    description: copy.description,
    state,
    started_at: state === 'pendente' ? date : null,
    completed_at: state === 'concluido' ? date : null,
    canceled_at: state === 'cancelado' ? date : null,
    current: true,
  };
};

const rentalOfferContexts = new Set([
  PROPERTY_OFFER_BUSINESS_CONTEXT.RENTAL,
  'rent',
  'aluguel',
  'locacao',
  'locação',
]);

const rentalNegotiationStatusAliases = Object.freeze({
  tenantdidntsentoffer: 'tenantDidntSentOffer',
  tenant_didnt_sent_offer: 'tenantDidntSentOffer',
  tenant_didnt_send_offer: 'tenantDidntSentOffer',
  waitingownerresponse: 'waitingOwnerResponse',
  waiting_owner_response: 'waitingOwnerResponse',
  owner_response_pending: 'waitingOwnerResponse',
  waitingtenantrespondcounteroffer: 'waitingTenantRespondCounterOffer',
  waiting_tenant_respond_counter_offer: 'waitingTenantRespondCounterOffer',
  waitingownerrespondcounteroffer: 'waitingOwnerRespondCounterOffer',
  waiting_owner_respond_counter_offer: 'waitingOwnerRespondCounterOffer',
  ownerrejectedoffer: 'ownerRejectedOffer',
  owner_rejected_offer: 'ownerRejectedOffer',
  tenantrejectedoffer: 'tenantRejectedOffer',
  tenant_rejected_offer: 'tenantRejectedOffer',
  waitingfortenanttodecide: 'waitingForTenantToDecide',
  waiting_for_tenant_to_decide: 'waitingForTenantToDecide',
  offerdemandpending: 'offerDemandPending',
  offer_demand_pending: 'offerDemandPending',
  offeraccepted: 'offerAccepted',
  offer_accepted: 'offerAccepted',
  proposalcancelled: 'proposalCancelled',
  proposal_cancelled: 'proposalCancelled',
  stalledrejection: 'stalledRejection',
  stalled_rejection: 'stalledRejection',
  houseinactive: 'houseInactive',
  house_inactive: 'houseInactive',
  houseunpublished: 'houseUnpublished',
  house_unpublished: 'houseUnpublished',
});

const rentalDocumentationStatusAliases = Object.freeze({
  waitingdocumentations: 'waitingDocumentations',
  waiting_documentations: 'waitingDocumentations',
  waitingtenantdocumentation: 'waitingTenantDocumentation',
  waiting_tenant_documentation: 'waitingTenantDocumentation',
  waitingownerdocumentation: 'waitingOwnerDocumentation',
  waiting_owner_documentation: 'waitingOwnerDocumentation',
  waitingresenddocumentations: 'waitingResendDocumentations',
  waiting_resend_documentations: 'waitingResendDocumentations',
  waitingtenantresenddocumentation: 'waitingTenantResendDocumentation',
  waiting_tenant_resend_documentation: 'waitingTenantResendDocumentation',
  waitingownerresenddocumentation: 'waitingOwnerResendDocumentation',
  waiting_owner_resend_documentation: 'waitingOwnerResendDocumentation',
  documentationsinanalysis: 'documentationsInAnalysis',
  documentations_in_analysis: 'documentationsInAnalysis',
  documentationsrefused: 'documentationsRefused',
  documentations_refused: 'documentationsRefused',
  tenantdocumentationinanalysischip: 'tenantDocumentationInAnalysisChip',
  tenant_documentation_in_analysis_chip: 'tenantDocumentationInAnalysisChip',
  ownerdocumentationinanalysischip: 'ownerDocumentationInAnalysisChip',
  owner_documentation_in_analysis_chip: 'ownerDocumentationInAnalysisChip',
  waitingcontract: 'waitingContract',
  waiting_contract: 'waitingContract',
  tenantapproveddocumentation: 'tenantApprovedDocumentation',
  tenant_approved_documentation: 'tenantApprovedDocumentation',
  tenantdeadlinedocumentation: 'tenantDeadlineDocumentation',
  tenant_deadline_documentation: 'tenantDeadlineDocumentation',
  tenantdeadlineresenddocumentation: 'tenantDeadlineResendDocumentation',
  tenant_deadline_resend_documentation: 'tenantDeadlineResendDocumentation',
  tenantdocumentationinanalysis: 'tenantDocumentationInAnalysis',
  tenant_documentation_in_analysis: 'tenantDocumentationInAnalysis',
  tenantrejected: 'tenantRejected',
  tenant_rejected: 'tenantRejected',
  tenantwithmultipledocs: 'tenantWithMultipleDocs',
  tenant_with_multiple_docs: 'tenantWithMultipleDocs',
  waitingfortenanttodecide: 'waitingForTenantToDecide',
  waiting_for_tenant_to_decide: 'waitingForTenantToDecide',
  ownerapproveddocumentation: 'ownerApprovedDocumentation',
  owner_approved_documentation: 'ownerApprovedDocumentation',
  ownerdeadlinedocumentation: 'ownerDeadlineDocumentation',
  owner_deadline_documentation: 'ownerDeadlineDocumentation',
  ownerdeadlineresenddocumentation: 'ownerDeadlineResendDocumentation',
  owner_deadline_resend_documentation: 'ownerDeadlineResendDocumentation',
  ownerdocumentationinanalysis: 'ownerDocumentationInAnalysis',
  owner_documentation_in_analysis: 'ownerDocumentationInAnalysis',
});

const rentalRejectionReasonCopy = Object.freeze({
  TenantDoesntAgreeWithValue: 'Interessado não concorda com o valor do aluguel',
  TenantDoesntAgreeWithConditions:
    'Interessado não concorda com as negociações propostas pelo proprietário',
  TenantGaveUpApartment: 'Interessado desistiu de alugar este imóvel',
  TenantRentedAnother5A: 'Interessado alugou outro imóvel',
  TenantRentedAnother: 'Interessado alugou outro imóvel',
  TenantRentedAnotherOutside: 'Interessado alugou outro imóvel fora do Imudei',
  TenantGaveUpRenting: 'Interessado desistiu de alugar nesse momento',
  TenantStalled: 'Interessado não respondeu dentro do prazo de 72h',
  OwnerDoesntAgreeWithValue:
    'Proprietário não concorda com o valor de aluguel sugerido',
  OwnerDoesntAgreeWithConditions:
    'Proprietário não concorda com as solicitações especiais do interessado',
  OwnerSoldApartment: 'Proprietário decidiu vender o imóvel',
  OwnerRentedForAnotherTenantOutside: 'Proprietário alugou fora do Imudei',
  OwnerRentedForAnotherTenant5A:
    'Proprietário alugou esse imóvel para outro interessado',
  OwnerGaveUpRenting: 'Proprietário desistiu de alugar esse imóvel',
  OwnerStalled: 'Proprietário não respondeu dentro do prazo de 72h',
  InactiveHouse: 'Proposta cancelada por suspensão do imóvel',
  UnpublishedHouse: 'Proposta cancelada por despublicação do imóvel',
  TenantDocumentationRejected: 'Recusado na análise de crédito',
  CreditEvaluationRejected: 'Recusado na análise de crédito',
});

const isRentalOffer = offer =>
  rentalOfferContexts.has(
    String(offer?.business_context || '').toLowerCase()
  ) || String(offer?.imovel_finalidade || '').toLowerCase() === 'aluguel';

const normalizeRentalNegotiationToken = value =>
  String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();

const normalizedRentalDocumentationToken = value =>
  normalizeRentalNegotiationToken(value);

const rentalNegotiationStatusKey = offer => {
  const metadata = offer?.metadata || {};
  const raw = [
    offer?.rent_progress_status,
    offer?.rentProgressStatus,
    offer?.status_key,
    offer?.statusKey,
    offer?.phase,
    metadata.rent_progress_status,
    metadata.rentProgressStatus,
    metadata.status_key,
    metadata.statusKey,
    metadata.phase,
  ].find(Boolean);
  const normalized = normalizeRentalNegotiationToken(raw);
  return rentalNegotiationStatusAliases[normalized] || null;
};

const valueFromObject = (object, keys = []) => {
  if (!object) return null;
  return keys.map(key => object[key]).find(present) || null;
};

const kycForOffer = offer =>
  offer?.kyc_verification ||
  (Array.isArray(offer?.kyc_verifications)
    ? offer.kyc_verifications[0]
    : null) ||
  offer?.documentation_status ||
  offer?.kyc_status;

const kycPayloadValue = (offer, keys = []) => {
  const kyc = kycForOffer(offer) || {};
  return (
    valueFromObject(kyc, keys) ||
    valueFromObject(kyc.metadata, keys) ||
    valueFromObject(kyc.result, keys)
  );
};

const roleSummaryKeys = role =>
  role === 'owner'
    ? [
        'owner_summary',
        'ownerSummary',
        'owner_status',
        'ownerStatus',
        'owner_documentation_status',
        'ownerDocumentationStatus',
        'owner_documentation_summary',
        'ownerDocumentationSummary',
      ]
    : [
        'tenant_summary',
        'tenantSummary',
        'tenant_status',
        'tenantStatus',
        'tenant_documentation_status',
        'tenantDocumentationStatus',
        'tenant_documentation_summary',
        'tenantDocumentationSummary',
      ];

const rentalDocumentationSummaryKey = (offer, role) => {
  const keys = roleSummaryKeys(role);
  const metadata = offer?.metadata || {};
  const raw =
    valueFromObject(offer, keys) ||
    valueFromObject(offer?.summary, keys) ||
    valueFromObject(metadata, keys) ||
    kycPayloadValue(offer, keys);
  const normalized = normalizedRentalDocumentationToken(raw);
  return rentalDocumentationStatusAliases[normalized] || null;
};

const deadlineKeysForRole = (role, resend = false) => {
  const prefix = role === 'owner' ? 'owner' : 'tenant';
  const camelPrefix = role === 'owner' ? 'owner' : 'tenant';
  const resendPart = resend ? '_resend' : '';
  const resendCamelPart = resend ? 'Resend' : '';
  const specific = [
    `${prefix}${resendPart}_documentation_deadline_at`,
    `${prefix}${resendPart}_documentation_deadline`,
    `${camelPrefix}${resendCamelPart}DocumentationDeadlineAt`,
    `${camelPrefix}${resendCamelPart}DocumentationDeadline`,
  ];

  return role === 'owner'
    ? [
        ...specific,
        'owner_deadline_documentation_at',
        'ownerDeadlineDocumentationAt',
        'owner_deadline_at',
        'ownerDeadlineAt',
        'docs_supply_deadline_at',
        'docsSupplyDeadlineAt',
      ]
    : [
        ...specific,
        'tenant_deadline_documentation_at',
        'tenantDeadlineDocumentationAt',
        'tenant_deadline_at',
        'tenantDeadlineAt',
        'offer_deadline',
        'offerDeadline',
        'docs_demand_deadline_at',
        'docsDemandDeadlineAt',
      ];
};

const rentalDocumentationDeadline = (offer, role, resend = false) => {
  const keys = deadlineKeysForRole(role, resend);
  return (
    valueFromObject(offer, keys) ||
    valueFromObject(offer?.metadata, keys) ||
    kycPayloadValue(offer, keys)
  );
};

const formatReferenceDeadline = (value, { hourSuffix = true } = {}) => {
  if (!present(value)) return null;
  const stringValue = String(value);
  const isoMatch = stringValue.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/
  );
  if (isoMatch) {
    const [, , month, day, hour, minute] = isoMatch;
    if (!hour || !minute) return null;
    return `${day}/${month} às ${hour}:${minute}${hourSuffix ? 'h' : ''}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const dateLabel = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  }).format(date);
  const hourLabel = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

  return `${dateLabel} às ${hourLabel}${hourSuffix ? 'h' : ''}`;
};

const rentalNegotiationDeadlineKeys = statusKey => {
  const baseKeys = [
    'offer_deadline',
    'offerDeadline',
    'response_deadline_at',
    'responseDeadlineAt',
    'response_deadline_date',
    'responseDeadlineDate',
  ];

  if (statusKey === 'waitingTenantRespondCounterOffer') {
    return [
      'tenant_counter_offer_deadline_at',
      'tenantCounterOfferDeadlineAt',
      'tenant_response_deadline_at',
      'tenantResponseDeadlineAt',
      ...baseKeys,
    ];
  }

  if (
    ['waitingOwnerResponse', 'waitingOwnerRespondCounterOffer'].includes(
      statusKey
    )
  ) {
    return [
      'owner_counter_offer_deadline_at',
      'ownerCounterOfferDeadlineAt',
      'owner_response_deadline_at',
      'ownerResponseDeadlineAt',
      ...baseKeys,
    ];
  }

  return [];
};

const rentalNegotiationDeadline = (offer, statusKey) => {
  const keys = rentalNegotiationDeadlineKeys(statusKey);
  return (
    valueFromObject(offer, keys) ||
    valueFromObject(offer?.metadata, keys) ||
    valueFromObject(offer?.summary, keys)
  );
};

const rentalNegotiationDeadlineDescription = (offer, statusKey) => {
  const config = {
    waitingOwnerResponse: {
      actor: 'Proprietário',
      action: 'responder a proposta',
    },
    waitingOwnerRespondCounterOffer: {
      actor: 'Proprietário',
      action: 'responder a contra-proposta',
    },
    waitingTenantRespondCounterOffer: {
      actor: 'Interessado',
      action: 'responder a contra-proposta',
    },
  }[statusKey];

  if (!config) return STEP_STATUS_COPY[statusKey]?.description;

  const deadline = formatReferenceDeadline(
    rentalNegotiationDeadline(offer, statusKey),
    { hourSuffix: false }
  );

  if (deadline) {
    return `${config.actor} tem até ${deadline} para ${config.action}`;
  }

  return `${config.actor} precisa ${config.action}`;
};

const rentalDocumentationDeadlineDescription = ({
  offer,
  role,
  resend = false,
}) => {
  const actor = role === 'owner' ? 'Proprietário' : 'Interessado';
  const verb = resend ? 'reenviar' : 'enviar';
  const deadline = formatReferenceDeadline(
    rentalDocumentationDeadline(offer, role, resend)
  );

  if (deadline) {
    return `${actor} tem até ${deadline} para ${verb} a documentação`;
  }
  return `${actor} precisa ${verb} a documentação`;
};

const rentalDocumentationRejectionDescription = offer => {
  const metadata = offer?.metadata || {};
  const kyc = kycForOffer(offer) || {};
  const rawReason = firstPresent(
    offer?.documentation_rejection_reason_label,
    offer?.kyc_rejection_reason_label,
    offer?.refusal_reason_label,
    metadata.documentation_rejection_reason_label,
    metadata.kyc_rejection_reason_label,
    kyc.metadata?.documentation_rejection_reason_label,
    kyc.metadata?.kyc_rejection_reason_label,
    offer?.documentation_rejection_reason,
    offer?.kyc_rejection_reason,
    metadata.documentation_rejection_reason,
    metadata.kyc_rejection_reason,
    kyc.metadata?.documentation_rejection_reason,
    kyc.metadata?.kyc_rejection_reason,
    kyc.result?.rejection_reason,
    kyc.result?.reason
  );

  return rentalRejectionReasonCopy[rawReason] || rawReason || null;
};

const contractForOffer = offer => {
  if (offer?.ccv_contract) return offer.ccv_contract;
  return Array.isArray(offer?.property_contracts)
    ? offer.property_contracts.find(contract =>
        ['ccv', 'rental_contract', 'rental'].includes(
          String(contract?.kind || '').toLowerCase()
        )
      ) || null
    : null;
};

const completedStep = ({ key, completedAt, label, description }) => ({
  key,
  label:
    label ||
    STEP_STATUS_COPY[key]?.startedLabel ||
    STEP_STATUS_COPY[key]?.label,
  description: description || STEP_STATUS_COPY[key]?.description,
  state: 'concluido',
  completed_at: completedAt || null,
  current: false,
});

const pendingStep = ({
  key,
  startedAt,
  current = false,
  label,
  description,
}) => ({
  key,
  label: label || STEP_STATUS_COPY[key]?.label,
  description: description || STEP_STATUS_COPY[key]?.description,
  state: 'pendente',
  started_at: startedAt || null,
  current,
});

const canceledStep = ({ key, canceledAt, label, description }) => ({
  key,
  label: label || STEP_STATUS_COPY[key]?.label,
  description: description || STEP_STATUS_COPY[key]?.description,
  state: 'cancelado',
  canceled_at: canceledAt || null,
  current: true,
});

const rentalKycStatus = offer => String(kycForOffer(offer)?.status || '');

const rentalKycDate = offer => {
  const kyc = kycForOffer(offer);
  return (
    kyc?.verified_at ||
    kyc?.updated_at ||
    kyc?.created_at ||
    offer?.accepted_at ||
    offer?.decided_at ||
    null
  );
};

const rentalDocumentationStep = offer => {
  const summaryKey = rentalDocumentationSummaryKey(offer, 'tenant');
  if (summaryKey === 'tenantApprovedDocumentation') {
    return completedStep({
      key: 'docsApproved',
      completedAt: rentalKycDate(offer),
      description: 'Interessado com documentação aprovada',
    });
  }
  if (summaryKey === 'tenantDeadlineDocumentation') {
    return pendingStep({
      key: 'docsDemandPending',
      startedAt: rentalKycDate(offer),
      current: !contractForOffer(offer),
      description: rentalDocumentationDeadlineDescription({
        offer,
        role: 'tenant',
      }),
    });
  }
  if (summaryKey === 'tenantDeadlineResendDocumentation') {
    return pendingStep({
      key: 'docsDemandResend',
      startedAt: rentalKycDate(offer),
      current: true,
      description: rentalDocumentationDeadlineDescription({
        offer,
        role: 'tenant',
        resend: true,
      }),
    });
  }
  if (summaryKey === 'waitingDocumentations') {
    return pendingStep({
      key: 'waitingDocumentations',
      startedAt: rentalKycDate(offer),
      current: true,
    });
  }
  if (summaryKey === 'waitingTenantDocumentation') {
    return pendingStep({
      key: 'waitingTenantDocumentation',
      startedAt: rentalKycDate(offer),
      current: !contractForOffer(offer),
    });
  }
  if (summaryKey === 'waitingResendDocumentations') {
    return pendingStep({
      key: 'waitingResendDocumentations',
      startedAt: rentalKycDate(offer),
      current: true,
    });
  }
  if (summaryKey === 'waitingTenantResendDocumentation') {
    return pendingStep({
      key: 'waitingTenantResendDocumentation',
      startedAt: rentalKycDate(offer),
      current: true,
    });
  }
  if (summaryKey === 'tenantDocumentationInAnalysis') {
    return pendingStep({
      key: 'docsQuintoandarAnalysis',
      startedAt: rentalKycDate(offer),
      current: true,
      description: 'Interessado com documentação em análise (3 dias úteis)',
    });
  }
  if (
    ['documentationsInAnalysis', 'tenantDocumentationInAnalysisChip'].includes(
      summaryKey
    )
  ) {
    return pendingStep({
      key: summaryKey,
      startedAt: rentalKycDate(offer),
      current: true,
    });
  }
  if (summaryKey === 'tenantRejected') {
    return canceledStep({
      key: 'tenantRejected',
      canceledAt: rentalKycDate(offer),
      description:
        rentalDocumentationRejectionDescription(offer) ||
        'Recusado na análise de crédito',
    });
  }
  if (summaryKey === 'documentationsRefused') {
    return canceledStep({
      key: 'documentationsRefused',
      canceledAt: rentalKycDate(offer),
    });
  }
  if (summaryKey === 'waitingForTenantToDecide') {
    return pendingStep({
      key: 'waitingForTenantToDecide',
      startedAt: rentalKycDate(offer),
      current: true,
    });
  }
  if (summaryKey === 'waitingContract') {
    return pendingStep({
      key: 'waitingContract',
      startedAt: rentalKycDate(offer),
      current: true,
    });
  }
  if (summaryKey === 'tenantWithMultipleDocs') {
    return pendingStep({
      key: 'tenantWithMultipleDocs',
      startedAt: rentalKycDate(offer),
      current: true,
    });
  }

  const status = rentalKycStatus(offer);
  if (status === 'approved') {
    return completedStep({
      key: 'docsApproved',
      completedAt: rentalKycDate(offer),
    });
  }
  if (['rejected', 'error'].includes(status)) {
    return pendingStep({
      key: 'docsDemandResend',
      startedAt: rentalKycDate(offer),
      current: true,
    });
  }
  if (status === 'pending') {
    return pendingStep({
      key: 'docsQuintoandarAnalysis',
      startedAt: rentalKycDate(offer),
      current: true,
    });
  }
  return pendingStep({
    key: 'docsDemandPending',
    startedAt: offer?.accepted_at || offer?.decided_at || null,
    current: !contractForOffer(offer),
  });
};

const rentalOwnerDocumentationStep = offer => {
  const summaryKey = rentalDocumentationSummaryKey(offer, 'owner');
  const contract = contractForOffer(offer);
  if (contract) {
    return completedStep({
      key: 'docsSupplyPending',
      label: 'Documentação do proprietário aprovada',
      completedAt:
        contract.created_at || contract.sent_at || contract.signed_at,
      description: 'Documentação pronta para o contrato',
    });
  }

  if (summaryKey === 'ownerApprovedDocumentation') {
    return completedStep({
      key: 'docsSupplyPending',
      label: 'Documentação do proprietário aprovada',
      completedAt: rentalKycDate(offer),
      description: 'Proprietário com documentação aprovada',
    });
  }
  if (summaryKey === 'ownerDeadlineDocumentation') {
    return pendingStep({
      key: 'docsSupplyPending',
      startedAt: rentalKycDate(offer),
      description: rentalDocumentationDeadlineDescription({
        offer,
        role: 'owner',
      }),
    });
  }
  if (summaryKey === 'ownerDeadlineResendDocumentation') {
    return pendingStep({
      key: 'docsSupplyPending',
      label: 'Documentos com pendência do proprietário',
      startedAt: rentalKycDate(offer),
      description: rentalDocumentationDeadlineDescription({
        offer,
        role: 'owner',
        resend: true,
      }),
    });
  }
  if (summaryKey === 'waitingOwnerDocumentation') {
    return pendingStep({
      key: 'waitingOwnerDocumentation',
      startedAt: rentalKycDate(offer),
    });
  }
  if (summaryKey === 'waitingOwnerResendDocumentation') {
    return pendingStep({
      key: 'waitingOwnerResendDocumentation',
      startedAt: rentalKycDate(offer),
    });
  }
  if (summaryKey === 'ownerDocumentationInAnalysis') {
    return pendingStep({
      key: 'docsSupplyPending',
      label: 'Documentação do proprietário em análise',
      startedAt: rentalKycDate(offer),
      description: 'Proprietário com documentação em análise (3 dias úteis)',
    });
  }
  if (summaryKey === 'ownerDocumentationInAnalysisChip') {
    return pendingStep({
      key: 'ownerDocumentationInAnalysisChip',
      startedAt: rentalKycDate(offer),
    });
  }

  return pendingStep({
    key: 'docsSupplyPending',
    startedAt: offer?.accepted_at || offer?.decided_at || null,
  });
};

const rentalGuaranteeStep = offer => {
  const guaranteeStatus = String(
    offer?.guarantee_status ||
      offer?.metadata?.guarantee_status ||
      offer?.metadata?.guaranteeStatus ||
      ''
  ).toLowerCase();
  const guaranteeDate =
    offer?.metadata?.guarantee_paid_at ||
    offer?.metadata?.guarantee_created_at ||
    offer?.updated_at ||
    null;

  if (['paid', 'paga', 'pago'].includes(guaranteeStatus)) {
    return completedStep({
      key: 'guaranteePaid',
      completedAt: guaranteeDate,
    });
  }
  if (
    ['payment_pending', 'pending_payment', 'aguardando_pagamento'].includes(
      guaranteeStatus
    )
  ) {
    return pendingStep({
      key: 'guaranteeDemandPaymentPending',
      startedAt: guaranteeDate,
      current: !contractForOffer(offer),
    });
  }
  if (['created', 'emitida', 'issued'].includes(guaranteeStatus)) {
    return pendingStep({
      key: 'guaranteeCreated',
      startedAt: guaranteeDate,
    });
  }

  return null;
};

const contractSignerStatus = signer =>
  String(signer?.status || signer?.signature_status || '').toLowerCase();

const contractSignerRole = signer =>
  String(signer?.role || signer?.kind || signer?.type || '').toLowerCase();

const rentalContractStep = offer => {
  const contract = contractForOffer(offer);
  if (!contract) {
    return pendingStep({
      key: 'contractQuintoandarInCreation',
      startedAt: offer?.accepted_at || offer?.decided_at || null,
      current: rentalKycStatus(offer) === 'approved',
    });
  }

  const status = String(contract.status || '').toLowerCase();
  if (status === 'signed') {
    return completedStep({
      key: 'contractStartDateReached',
      completedAt: contract.signed_at || contract.updated_at,
    });
  }
  if (['canceled', 'failed'].includes(status)) {
    return canceledStep({
      key: 'contractCancelled',
      canceledAt: contract.canceled_at || contract.updated_at,
    });
  }
  if (['terminated', 'finished', 'finalized', 'finalizado'].includes(status)) {
    return completedStep({
      key: 'contractTerminated',
      completedAt: contract.finished_at || contract.updated_at,
    });
  }
  if (status === 'pending') {
    return pendingStep({
      key: 'contractQuintoandarInCreation',
      startedAt: contract.created_at,
      current: true,
    });
  }

  const signers = Array.isArray(contract.signers) ? contract.signers : [];
  const unsignedSigners = signers.filter(
    signer => !['signed', 'assinado'].includes(contractSignerStatus(signer))
  );
  const hasUnsignedOwner = unsignedSigners.some(signer =>
    ['owner', 'supply', 'proprietario', 'proprietário'].includes(
      contractSignerRole(signer)
    )
  );
  const hasUnsignedDemand = unsignedSigners.some(signer =>
    ['tenant', 'demand', 'client', 'cliente', 'interessado'].includes(
      contractSignerRole(signer)
    )
  );
  if (signers.length && !hasUnsignedOwner && !hasUnsignedDemand) {
    return pendingStep({
      key: 'waitingSignaturesValidation',
      startedAt: contract.sent_at || contract.updated_at || contract.created_at,
      current: true,
    });
  }
  let key = 'contractAllSignaturesPending';
  if (hasUnsignedOwner && !hasUnsignedDemand)
    key = 'contractSupplySignaturePending';
  if (hasUnsignedDemand && !hasUnsignedOwner)
    key = 'contractDemandSignaturePending';

  return pendingStep({
    key,
    startedAt: contract.sent_at || contract.updated_at || contract.created_at,
    current: true,
  });
};

const rentalCanceledNegotiationKey = offer => {
  const explicitKey = rentalNegotiationStatusKey(offer);
  if (
    [
      'ownerRejectedOffer',
      'tenantRejectedOffer',
      'proposalCancelled',
      'stalledRejection',
      'houseInactive',
      'houseUnpublished',
    ].includes(explicitKey)
  ) {
    return explicitKey;
  }
  return offer?.status === PROPERTY_OFFER_STATUS.WITHDRAWN
    ? 'tenantRejectedOffer'
    : 'ownerRejectedOffer';
};

const rentalNegotiationStep = offer => {
  const statusKey = rentalNegotiationStatusKey(offer) || 'waitingOwnerResponse';
  const startedAt = offer?.proposal_date || offer?.submitted_at || null;
  if (
    [PROPERTY_OFFER_STATUS.DECLINED, PROPERTY_OFFER_STATUS.WITHDRAWN].includes(
      offer?.status
    )
  ) {
    return canceledStep({
      key: rentalCanceledNegotiationKey(offer),
      canceledAt: offer?.discarded_at || offer?.decided_at || null,
      description:
        offer?.refusal_reason_label ||
        offer?.summary?.canceled_reason_label ||
        STEP_STATUS_COPY[rentalCanceledNegotiationKey(offer)]?.description,
    });
  }

  return pendingStep({
    key: statusKey,
    startedAt,
    current: true,
    description: rentalNegotiationDeadlineDescription(offer, statusKey),
  });
};

const buildRentalOfferStatusSteps = offer => {
  if (offer?.status !== PROPERTY_OFFER_STATUS.ACCEPTED) {
    return [rentalNegotiationStep(offer)];
  }

  const submittedAt = offer?.proposal_date || offer?.submitted_at || null;
  const acceptedAt = offer?.accepted_at || offer?.decided_at || null;
  const steps = [
    completedStep({
      key: 'submitted',
      label: 'Proposta recebida',
      completedAt: submittedAt,
      description: 'Interessado enviou a proposta',
    }),
    completedStep({
      key: 'accepted',
      label: 'Proposta aceita',
      completedAt: acceptedAt,
      description: 'Proposta aceita pelo proprietário',
    }),
    rentalDocumentationStep(offer),
    rentalOwnerDocumentationStep(offer),
  ];
  const guaranteeStep = rentalGuaranteeStep(offer);
  if (guaranteeStep) steps.push(guaranteeStep);
  steps.push(rentalContractStep(offer));

  const currentIndex = steps.findIndex(step => step.current);
  if (currentIndex >= 0) {
    return steps.map((step, index) => ({
      ...step,
      current: index === currentIndex,
    }));
  }

  return steps;
};

const rentalMacroStatusLabel = offer => {
  const contract = contractForOffer(offer);
  if (contract) return 'Contrato';
  return 'Documentação';
};

const rentalOfferMacroStatusLabel = offer => {
  if (offer.macro_status_label) return offer.macro_status_label;
  if (offer.status === PROPERTY_OFFER_STATUS.ACCEPTED) {
    return rentalMacroStatusLabel(offer);
  }
  if (offer.status === PROPERTY_OFFER_STATUS.SUBMITTED) return 'Negociação';
  return 'Cancelada';
};

export const buildFallbackOfferStatusPayload = (offer = {}) => {
  if (isRentalOffer(offer)) {
    return {
      offer_id: offer.id,
      macro_status_label: rentalOfferMacroStatusLabel(offer),
      steps: buildRentalOfferStatusSteps(offer),
    };
  }

  return {
    offer_id: offer.id,
    macro_status_label:
      offer.macro_status_label ||
      labelForMacroStatus(
        getStatusByStep(
          resolveOfferStepKey(offer),
          offer.ccv || offer.ccv_contract
        )
      ),
    steps: [buildFallbackOfferStatusStep(offer)],
  };
};

const saleContexts = new Set([
  PROPERTY_OFFER_BUSINESS_CONTEXT.PURCHASE,
  'sale',
  'sales',
  'venda',
  'comprar',
  'compra',
]);

export const isPurchaseOfferContext = context =>
  saleContexts.has(String(context || '').toLowerCase());

export const isLongHouseId = id => /^\d{7,}$/.test(String(id || ''));

export const longToShortId = id =>
  isLongHouseId(id) ? Number(id).toString(36).toUpperCase() : String(id || '');

const formatFirstName = value =>
  String(value || '')
    .trim()
    .split(/\s+/)[0] || 'cliente';

const normalizedMessageAddress = value =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

export const getOfferDeepLink = ({
  accountId,
  businessContext,
  imovelId,
  offerId,
  contactId,
  origin = '',
} = {}) => {
  if (offerId) {
    return `${origin}${frontendURL(
      `accounts/${accountId}/propostas/${offerId}`
    )}`;
  }

  const path = isPurchaseOfferContext(businessContext)
    ? `accounts/${accountId}/propostas/venda`
    : `accounts/${accountId}/propostas/aluguel`;
  const params = new URLSearchParams();
  if (imovelId) params.set('imovel_id', imovelId);
  if (contactId) params.set('contact_id', contactId);
  params.set('new', '1');

  return `${origin}${frontendURL(path)}?${params.toString()}`;
};

export const buildOfferMessage = ({
  accountId,
  businessContext,
  contactName = 'cliente',
  address = 'imóvel visitado',
  imovelId,
  offerId,
  contactId,
  origin = '',
} = {}) => {
  const link = getOfferDeepLink({
    accountId,
    businessContext,
    imovelId,
    offerId,
    contactId,
    origin,
  });
  const visitorName = formatFirstName(contactName);
  const houseShortId = longToShortId(imovelId);
  const houseDescription =
    [houseShortId, normalizedMessageAddress(address)]
      .filter(Boolean)
      .join(', ') || 'imóvel visitado';

  if (isPurchaseOfferContext(businessContext)) {
    return [
      `Olá ${visitorName}, segue o link para que você possa fazer sua proposta para o`,
      `imóvel ${houseDescription}:`,
      link,
    ].join('\n');
  }

  return [
    `Olá ${visitorName}, segue o link para que você possa fazer sua proposta para o`,
    `imóvel ${houseDescription}: ${link}.`,
    'Assim que sua proposta for aceita você pode reservar o imóvel para não correr o risco de perdê-lo.',
    'Saiba mais sobre a reserva: https://quin.to/faq-reserva-inq',
  ].join('\n');
};

export const paymentKindForReferenceValue = referenceValue => {
  const value = String(referenceValue || '').toUpperCase();
  return (
    {
      CASH: PROPERTY_OFFER_PAYMENT_KIND.CASH,
      FINANCED: PROPERTY_OFFER_PAYMENT_KIND.FINANCING,
      CASH_USING_FGTS: PROPERTY_OFFER_PAYMENT_KIND.CASH_FGTS,
      FINANCED_USING_FGTS: PROPERTY_OFFER_PAYMENT_KIND.FINANCING_FGTS,
    }[value] || referenceValue
  );
};

export default getOfferStatusDescriptor;
